import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Drawer,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  LinearProgress,
  Radio,
  RadioGroup,
  Slider,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import * as c from "../../../../constants";
import { useTranslation } from "../../../../language";
import ButtonForm from "../../../../components/Button/ButtonForm";

const mappedPrefill = (val) => {
  if (!val) return "";
  if (val === "M") return "male";
  if (val === "F") return "female";
  return val;
};

export default function NewQuestionnaireDrawer({ open, onClose, onSuccess }) {
  const theme = useTheme();
  const { t, language } = useTranslation();

  const [questionnaire, setQuestionnaire] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(false);
  const questionRefs = useRef({});

  const slug = language === "de" ? "speakstart_de" : "speakstart_en";

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSuccess(false);
    setSubmitError(null);
    setAnswers({});
    setQuestionnaire(null);
    axios
      .get(`${c.API_URL}/api/questionnaires/${slug}/`)
      .then((res) => setQuestionnaire(res.data))
      .catch(() => setSubmitError(t("bt_results_error")))
      .finally(() => setLoading(false));
  }, [open, slug]);

  const questions = useMemo(() => {
    if (!questionnaire) return [];
    return questionnaire.questions.map((qq) => ({
      id: qq.question.id,
      key: qq.id,
      code: qq.question.code,
      text: qq.question.text,
      question_type: qq.question.question_type,
      options: qq.question.options || [],
      required: qq.required,
      order: qq.order,
      depends_on_code: qq.depends_on_code,
      depends_on_value: qq.depends_on_value,
      prefilled_value: qq.question.prefilled_value || null,
    }));
  }, [questionnaire]);

  useEffect(() => {
    if (!questionnaire) return;
    const initialAnswers = {};
    questionnaire.questions.forEach((qq) => {
      const prefill = qq.question.prefilled_value;
      if (prefill !== null && prefill !== undefined) {
        initialAnswers[qq.question.code] = mappedPrefill(prefill);
      }
    });
    setAnswers(initialAnswers);
  }, [questionnaire]);

  const handleChange = (code, value) =>
    setAnswers((prev) => ({ ...prev, [code]: value }));

  const isQuestionVisible = (q) => {
    if (!q.depends_on_code) return true;
    const parentValue = answers[q.depends_on_code];
    if (!parentValue) return false;
    const parentValues = Array.isArray(parentValue) ? parentValue : [parentValue];
    const dependsValues = Array.isArray(q.depends_on_value)
      ? q.depends_on_value
      : [q.depends_on_value];
    return parentValues.some((val) => dependsValues.includes(val));
  };

  const visibleQuestions = questions.filter(isQuestionVisible);

  const isAnswered = (q) => {
    if (!q.required) return true;
    const value = answers[q.code];
    if (q.question_type === "multi") return Array.isArray(value) && value.length > 0;
    return value !== undefined && value !== null && value !== "";
  };

  const answeredCount = visibleQuestions.filter(isAnswered).length;
  const progress =
    visibleQuestions.length > 0
      ? Math.round((answeredCount / visibleQuestions.length) * 100)
      : 0;

  const scrollToFirstUnanswered = () => {
    const first = visibleQuestions.find((q) => !isAnswered(q));
    if (first && questionRefs.current[first.code]) {
      questionRefs.current[first.code].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleSubmit = async () => {
    if (answeredCount !== visibleQuestions.length) {
      scrollToFirstUnanswered();
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await axios.post(`${c.API_URL}/api/questionnaire/submit/`, {
        questionnaire: questionnaire.id,
        answers: Object.entries(answers).map(([code, value]) => {
          const q = questions.find((q) => q.code === code);
          const finalValue = q.question_type === "boolean" ? value === "true" : value;
          return { question: q.id, value: finalValue };
        }),
      });
      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (err) {
      setSubmitError(err.response?.data?.message || t("bt_results_error"));
    } finally {
      setSubmitting(false);
    }
  };

  const todayStr = new Date().toLocaleDateString(
    language === "de" ? "de-DE" : "en-GB",
    { year: "numeric", month: "long", day: "numeric" }
  );

  const borderColor =
    theme.palette.mode === "light" ? theme.palette.grey.C900 : theme.palette.greydark.C400;

  const renderQuestion = (q) => (
    <Box key={q.key} ref={(el) => (questionRefs.current[q.code] = el)} sx={{ mb: 4 }}>
      <FormControl fullWidth>
        <FormLabel>
          {q.text} {q.required && "*"}
        </FormLabel>

        {q.question_type === "text" && (
          <TextField
            multiline
            required={q.required}
            value={answers[q.code] || ""}
            onChange={(e) => handleChange(q.code, e.target.value)}
            sx={{ mt: 2, mb: 3 }}
          />
        )}

        {q.question_type === "number" && (
          <TextField
            type="number"
            value={answers[q.code] || ""}
            onChange={(e) => handleChange(q.code, Number(e.target.value))}
            sx={{ mt: 2 }}
          />
        )}

        {q.question_type === "date" && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={answers[q.code] ? dayjs(answers[q.code], "YYYY-MM-DD", true) : null}
              onChange={(val) => handleChange(q.code, val ? val.format("YYYY-MM-DD") : "")}
              slotProps={{
                textField: { fullWidth: true, sx: { mt: 2, mb: 3 }, format: "YYYY-MM-DD" },
              }}
            />
          </LocalizationProvider>
        )}

        {q.question_type === "single" && (
          <RadioGroup
            value={answers[q.code] ? mappedPrefill(answers[q.code]) : mappedPrefill(q.prefilled_value)}
            onChange={(e) => handleChange(q.code, e.target.value)}
          >
            {q.options.map((opt) => (
              <FormControlLabel key={opt.value} value={opt.value} control={<Radio />} label={opt.label} />
            ))}
          </RadioGroup>
        )}

        {q.question_type === "multi" &&
          q.options.map((opt) => (
            <FormControlLabel
              key={opt.value}
              control={
                <Checkbox
                  checked={(answers[q.code] || []).includes(opt.value)}
                  onChange={(e) => {
                    const current = answers[q.code] || [];
                    handleChange(
                      q.code,
                      e.target.checked
                        ? [...current, opt.value]
                        : current.filter((v) => v !== opt.value)
                    );
                  }}
                />
              }
              label={opt.label}
            />
          ))}

        {q.question_type === "boolean" && (
          <RadioGroup
            value={answers[q.code] || ""}
            onChange={(e) => handleChange(q.code, e.target.value.toLowerCase())}
          >
            {q.options.map((opt) => (
              <FormControlLabel
                key={opt.id}
                value={String(opt.value).toLowerCase()}
                control={<Radio />}
                label={opt.label}
              />
            ))}
            {!q.required && (
              <FormControlLabel value="" control={<Radio />} label="—" />
            )}
          </RadioGroup>
        )}

        {q.question_type === "scale" && (
          <Slider
            min={1}
            max={10}
            step={1}
            marks
            value={answers[q.code] ?? 1}
            onChange={(_, value) => handleChange(q.code, value)}
            valueLabelDisplay="auto"
            sx={{
              mt: 3,
              "& .MuiSlider-valueLabel": { fontSize: "1.2rem", fontWeight: 600 },
              "& .MuiSlider-markLabel": { fontSize: "1rem" },
            }}
          />
        )}
      </FormControl>
    </Box>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 520 },
          display: "flex",
          flexDirection: "column",
          ...(theme.palette.mode === "dark" && {
            bgcolor: "greydark.C900",
            borderLeft: "1px solid",
            borderColor: "greydark.C400",
          }),
        },
      }}
    >
      {/* Sticky header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${borderColor}`,
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: theme.palette.mode === "light" ? "grey.C50" : "greydark.C900",
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {t("quest_new_drawer_title")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {todayStr}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Scrollable body */}
      <Box sx={{ px: 3, py: 3, overflowY: "auto", flex: 1 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && success && (
          <Alert severity="success" sx={{ mt: 4 }}>
            <Typography variant="body1" fontWeight={600}>
              {t("quest_new_success")}
            </Typography>
          </Alert>
        )}

        {!loading && !success && questionnaire && (
          <>
            <LinearProgress variant="determinate" value={progress} sx={{ mb: 1 }} />
            <Typography variant="subtitle2" align="center" fontWeight={400} sx={{ mb: 4 }}>
              {answeredCount} / {visibleQuestions.length} {t("quest_answered")}
            </Typography>

            {visibleQuestions.map(renderQuestion)}

            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {submitError}
              </Alert>
            )}

            <ButtonForm
              name={submitting ? t("onb_quest_submitting") : t("onb_quest_submit")}
              onClick={handleSubmit}
              disabled={submitting}
            />
          </>
        )}

        {!loading && !questionnaire && !success && submitError && (
          <Alert severity="error" sx={{ mt: 4 }}>
            {submitError}
          </Alert>
        )}
      </Box>
    </Drawer>
  );
}
